/**
 * What a refused Cancel or Remove means, and what a person reads for it: a plain sentence, a
 * code and the job reference. An admin also reads the technical line (owner, 2026-09-25).
 */
import { codeAndReference, errorDetail, type ErrorCode } from "./errorCode.js";

export type Refusal = "gone" | "finished" | "unsupported" | "failed";

/** The sentence, and the code when the refusal itself names it; a failed request's code comes
 *  from its own detail. */
const REFUSALS: Record<Exclude<Refusal, "gone">, { text: string; code?: ErrorCode }> = {
  finished: { text: "This generation already finished.", code: "GEN-REVIEW" },
  unsupported: { text: "Cancel and Remove are not available yet.", code: "GEN-SETUP" },
  failed: { text: "That did not work. Try again." },
};

/** A cloud that has the routes answers a missing job with this detail; an older cloud has no
 *  such route, and answers 404 or 405 without it. */
const JOB_NOT_FOUND = "Job not found";

/** The cloud itself says the job is gone. A 404 without the detail is a proxy's, during a
 *  redeploy: the job may be alive, so it is an ordinary failed request. */
export function jobGone(e: unknown): boolean {
  return (e as { status?: unknown } | null)?.status === 404 && errorDetail(e) === JOB_NOT_FOUND;
}

export function refusalOf(e: unknown): Refusal {
  const status = (e as { status?: unknown } | null)?.status;
  if (status === 409) return "finished";
  if (jobGone(e)) return "gone";
  return status === 404 || status === 405 ? "unsupported" : "failed";
}

/** A magenta retry the cloud refused: the job has no first image, is running, or moved. */
export function retryRefusedText(reference: string): string {
  return `The print guide step cannot run again for this generation. ${codeAndReference("GEN-REVIEW", reference)}`;
}

/** The 409s of the Only mock-ups page, which are not about a finished generation. */
const CONFLICTS: Record<string, string> = {
  not_editable: "Only a finished Mock-up only image can be relabelled.",
  not_regenerable: "Only a finished image can be made again.",
};

/** The note for a refused action on the job `reference`. Not for a job that is gone. */
export function refusalText(e: unknown, { admin, reference }: { admin: boolean; reference: string }): string {
  const refusal = refusalOf(e);
  const detail = errorDetail(e);
  const conflict = refusal === "finished" ? CONFLICTS[detail] : undefined;
  const { text, code } = conflict
    ? { text: conflict, code: REFUSALS.finished.code }
    : REFUSALS[refusal === "gone" ? "failed" : refusal];
  const shown = `${text} ${codeAndReference(code ?? detail, reference)}`;
  if (!admin) return shown;
  return `${shown} ${refusal === "unsupported" ? `The cloud needs an update (${detail}).` : detail}`;
}
